import {
  ChangeEvent,
  ClipboardEvent,
  FC,
  KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useTranslation } from 'react-i18next';
import { NsKey } from 'enums/NsKey';
import { Button } from 'components/ui/Button';
import { GenericLoading, Sizes } from 'components/ui/Loader';
import { MessageSquare, RefreshCw } from 'lucide-react';

const CODE_LENGTH = 6;
const RESEND_COOLDOWN_SECONDS = 60;

export interface SmsCodeStepProps {
  phoneMasked?: string | null;
  sentAt: number;
  expiresInSeconds: number;
  onSubmit: (code: string) => Promise<void>;
  onResend: () => Promise<void>;
  isVerifying: boolean;
  isResending: boolean;
  errorMessage?: string | null;
  codeInvalidated: boolean;
}

const formatCountdown = (totalSeconds: number): string => {
  const safe = Math.max(0, totalSeconds);
  const minutes = Math.floor(safe / 60);
  const seconds = safe % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

const SmsCodeStep: FC<SmsCodeStepProps> = ({
  phoneMasked,
  sentAt,
  expiresInSeconds,
  onSubmit,
  onResend,
  isVerifying,
  isResending,
  errorMessage,
  codeInvalidated
}) => {
  const { t } = useTranslation(NsKey.MncapMedicalValidation);
  const [digits, setDigits] = useState<string[]>(() => Array(CODE_LENGTH).fill(''));
  const [now, setNow] = useState<number>(() => Date.now());
  const inputsRef = useRef<(HTMLInputElement | null)[]>([]);
  const hasAutoSubmittedRef = useRef<string | null>(null);

  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    setDigits(Array(CODE_LENGTH).fill(''));
    hasAutoSubmittedRef.current = null;
    inputsRef.current[0]?.focus();
  }, [sentAt]);

  const elapsedSeconds = Math.floor((now - sentAt) / 1000);
  const remainingExpiry = expiresInSeconds - elapsedSeconds;
  const remainingCooldown = RESEND_COOLDOWN_SECONDS - elapsedSeconds;
  const isExpired = remainingExpiry <= 0 || codeInvalidated;
  const canResend = !isResending && (remainingCooldown <= 0 || isExpired);

  const code = useMemo(() => digits.join(''), [digits]);
  const isComplete = code.length === CODE_LENGTH && digits.every(d => d !== '');

  const updateDigit = useCallback(
    (index: number, value: string) => {
      setDigits(prev => {
        const next = [...prev];
        next[index] = value;
        return next;
      });
    },
    []
  );

  const handleChange = useCallback(
    (index: number) => (e: ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value.replace(/\D/g, '');
      if (!raw) {
        updateDigit(index, '');
        return;
      }
      if (raw.length === 1) {
        updateDigit(index, raw);
        if (index < CODE_LENGTH - 1) inputsRef.current[index + 1]?.focus();
        return;
      }
      setDigits(prev => {
        const next = [...prev];
        const chars = raw.slice(0, CODE_LENGTH - index).split('');
        chars.forEach((c, i) => {
          next[index + i] = c;
        });
        return next;
      });
      const lastIndex = Math.min(index + raw.length, CODE_LENGTH) - 1;
      inputsRef.current[lastIndex]?.focus();
    },
    [updateDigit]
  );

  const handleKeyDown = useCallback(
    (index: number) => (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Backspace' && !digits[index] && index > 0) {
        inputsRef.current[index - 1]?.focus();
      } else if (e.key === 'ArrowLeft' && index > 0) {
        inputsRef.current[index - 1]?.focus();
      } else if (e.key === 'ArrowRight' && index < CODE_LENGTH - 1) {
        inputsRef.current[index + 1]?.focus();
      }
    },
    [digits]
  );

  const handlePaste = useCallback((e: ClipboardEvent<HTMLInputElement>) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, CODE_LENGTH);
    if (!pasted) return;
    e.preventDefault();
    const next = Array(CODE_LENGTH).fill('');
    pasted.split('').forEach((c, i) => {
      next[i] = c;
    });
    setDigits(next);
    const focusIndex = Math.min(pasted.length, CODE_LENGTH - 1);
    inputsRef.current[focusIndex]?.focus();
  }, []);

  useEffect(() => {
    if (!isComplete || isVerifying || isExpired) return;
    if (hasAutoSubmittedRef.current === code) return;
    hasAutoSubmittedRef.current = code;
    onSubmit(code);
  }, [isComplete, code, isVerifying, isExpired, onSubmit]);

  return (
    <div className="w-full max-w-md mx-auto bg-white rounded-2xl shadow-xl p-6 sm:p-8">
      <div className="flex flex-col items-center text-center mb-6">
        <div className="bg-primary/10 rounded-full p-3 mb-4">
          <MessageSquare className="w-7 h-7 text-primary" />
        </div>
        <h1 className="text-xl font-bold text-gray-900">
          {t('MNCAP_MEDICAL_VALIDATION.SMS.TITLE')}
        </h1>
        {phoneMasked && (
          <p className="text-sm text-gray-600 mt-2">
            {t('MNCAP_MEDICAL_VALIDATION.SMS.SUBTITLE', { phone: phoneMasked })}
          </p>
        )}
      </div>

      <div className="flex justify-center gap-2 sm:gap-3 mb-4" onPaste={handlePaste}>
        {digits.map((digit, i) => (
          <input
            key={i}
            ref={el => {
              inputsRef.current[i] = el;
            }}
            type="text"
            inputMode="numeric"
            autoComplete={i === 0 ? 'one-time-code' : 'off'}
            maxLength={1}
            value={digit}
            onChange={handleChange(i)}
            onKeyDown={handleKeyDown(i)}
            disabled={isVerifying || isExpired}
            aria-label={`${t('MNCAP_MEDICAL_VALIDATION.SMS.CODE_LABEL')} ${i + 1}`}
            className="w-11 h-12 sm:w-12 sm:h-14 text-center text-xl font-semibold rounded-lg border border-gray-300 bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary disabled:opacity-60 disabled:cursor-not-allowed"
          />
        ))}
      </div>

      <div className="min-h-[1.5rem] text-center text-sm mb-2">
        {isExpired ? (
          <span className="text-red-600">
            {t('MNCAP_MEDICAL_VALIDATION.SMS.CODE_EXPIRED_MESSAGE')}
          </span>
        ) : (
          <span className="text-gray-600">
            {t('MNCAP_MEDICAL_VALIDATION.SMS.EXPIRES_IN', {
              time: formatCountdown(remainingExpiry)
            })}
          </span>
        )}
      </div>

      {errorMessage && !isExpired && (
        <div className="bg-red-50 border-l-4 border-red-500 rounded-r-lg p-3 text-sm text-red-700 mb-4">
          {errorMessage}
        </div>
      )}

      {isVerifying && (
        <div className="flex items-center justify-center gap-2 text-sm text-gray-600 mb-4">
          <GenericLoading size={Sizes.sm} isInline loading />
          <span>{t('MNCAP_MEDICAL_VALIDATION.SMS.VERIFYING')}</span>
        </div>
      )}

      <div className="flex flex-col items-center gap-3">
        <Button
          outline
          onClick={() => onResend()}
          disabled={!canResend}
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          {canResend
            ? t('MNCAP_MEDICAL_VALIDATION.SMS.RESEND')
            : t('MNCAP_MEDICAL_VALIDATION.SMS.RESEND_COOLDOWN', {
                seconds: Math.max(0, remainingCooldown)
              })}
        </Button>
      </div>
    </div>
  );
};

export default SmsCodeStep;
